<script setup lang="ts">
interface Props {
  modelValue?: string;
  length?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  inputClass?: string;
  containerClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  length: 5,
  autoFocus: true,
  disabled: false,
  inputClass:
    "otp-input size-12 text-center focus:outline-none border-0 form-input rounded-lg placeholder-gray-500 text-sm mx-1.5 p-3.5 bg-white text-gray-900 ring-1 ring-inset ring-gray-200 focus:ring-primary-500",
  containerClass: "flex",
});

const emit = defineEmits<{
  (e: "update:modelValue" | "complete", value: string): void;
}>();

const digits = ref<string[]>(
  Array.from({ length: props.length }, (_, i) => props.modelValue?.[i] ?? "")
);
const inputRefs = ref<HTMLInputElement[]>([]);

const setRef = (
  el: Element | ComponentPublicInstance | null,
  index: number
) => {
  if (el) inputRefs.value[index] = el as HTMLInputElement;
};

const syncFromModel = (value: string) => {
  const next = Array.from({ length: props.length }, (_, i) => value[i] ?? "");
  if (next.join("") !== digits.value.join("")) {
    digits.value = next;
  }
};

const emitChange = () => {
  const joined = digits.value.join("");
  emit("update:modelValue", joined);
  if (joined.length === props.length) {
    emit("complete", joined);
  }
};

const focusIndex = (index: number) => {
  const clamped = Math.max(0, Math.min(props.length - 1, index));
  const el = inputRefs.value[clamped];
  if (el) {
    el.focus();
    el.select?.();
  }
};

const fillDigits = (text: string, fromIndex: number) => {
  const cleaned = text.replace(/\D/g, "");
  if (!cleaned) return;
  let cursor = fromIndex;
  for (let i = 0; i < cleaned.length && cursor < props.length; i++, cursor++) {
    digits.value[cursor] = cleaned[i];
  }
  emitChange();
  focusIndex(Math.min(cursor, props.length - 1));
};

const onInput = (event: Event, index: number) => {
  const target = event.target as HTMLInputElement;
  const cleaned = target.value.replace(/\D/g, "");

  if (cleaned.length > 1) {
    fillDigits(cleaned, index);
    target.value = digits.value[index] ?? "";
    return;
  }

  const digit = cleaned.slice(0, 1);
  digits.value[index] = digit;
  target.value = digit;
  emitChange();
  if (digit && index < props.length - 1) {
    focusIndex(index + 1);
  }
};

const onKeydown = (event: KeyboardEvent, index: number) => {
  if (event.ctrlKey || event.metaKey) return;
  const key = event.key;

  if (key === "Backspace") {
    if (digits.value[index]) {
      digits.value[index] = "";
      emitChange();
    } else if (index > 0) {
      event.preventDefault();
      digits.value[index - 1] = "";
      emitChange();
      focusIndex(index - 1);
    }
    return;
  }
  if (key === "Delete") {
    if (digits.value[index]) {
      digits.value[index] = "";
      emitChange();
    }
    return;
  }
  if (key === "ArrowLeft") {
    event.preventDefault();
    focusIndex(index - 1);
    return;
  }
  if (key === "ArrowRight") {
    event.preventDefault();
    focusIndex(index + 1);
    return;
  }
  if (key === "Home") {
    event.preventDefault();
    focusIndex(0);
    return;
  }
  if (key === "End") {
    event.preventDefault();
    focusIndex(props.length - 1);
    return;
  }
  if (key?.length === 1 && !/\d/.test(key)) {
    event.preventDefault();
  }
};

const onPaste = (event: ClipboardEvent, index: number) => {
  const text = event.clipboardData?.getData("text/plain") ?? "";
  if (!text) return;
  event.preventDefault();
  fillDigits(text, index);
};

const onFocus = (event: FocusEvent) => {
  (event.target as HTMLInputElement).select?.();
};

watch(
  () => props.modelValue,
  (value) => syncFromModel(value ?? "")
);

watch(
  () => props.length,
  (newLen) => {
    const current = digits.value.join("");
    digits.value = Array.from({ length: newLen }, (_, i) => current[i] ?? "");
  }
);

onMounted(() => {
  if (!props.autoFocus) return;
  nextTick(() => {
    const firstEmpty = digits.value.findIndex((d) => !d);
    focusIndex(firstEmpty === -1 ? props.length - 1 : firstEmpty);
  });
});
</script>

<template>
  <div :class="containerClass" dir="ltr">
    <input
      v-for="(digit, index) in digits"
      :key="index"
      :ref="(el) => setRef(el, index)"
      type="tel"
      inputmode="numeric"
      :autocomplete="index === 0 ? 'one-time-code' : 'off'"
      :maxlength="index === 0 ? length : 1"
      pattern="[0-9]*"
      :value="digit"
      :disabled="disabled"
      :class="inputClass"
      :data-otp-index="index"
      @input="(e) => onInput(e, index)"
      @keydown="(e) => onKeydown(e, index)"
      @paste="(e) => onPaste(e, index)"
      @focus="onFocus"
    >
  </div>
</template>
